/**
 * Source Agent
 *
 * An independent per-source agent that handles querying a single data source.
 * It receives an intent from the main agent, has the full schema for its source,
 * uses its OWN LLM call with tool calling to generate and execute queries,
 * retries on failure independently, and returns clean, limited data.
 *
 * For very_large sources (500+ tables), the source agent also gets a search_schema
 * tool to look up table/column details by keyword before writing queries.
 *
 * The main agent never sees: schema, SQL, errors, retries — only the final data.
 */

import { LLM } from '../../llm';
import { promptLoader } from '../prompt-loader';
import { getCurrentDateTimeForPrompt } from '../../utils/datetime';
import { logger } from '../../utils/logger';
import { extractPromptText } from '../prompt-extractor';
import { StreamBuffer, streamDelay, withProgressHeartbeat } from '../stream-buffer';
import { formatToolResultForLLM, formatQueryResultForLLM } from '../llm-result-truncator';
import type { ExternalTool, ExecutedToolInfo } from '../services/tool-executor-service';
import type { SourceAgentResult, AgentConfig, SourceToolInput } from './types';
import { TOOL_TRACKING_SAMPLE_ROWS, STREAM_PREVIEW_MAX_ROWS, STREAM_PREVIEW_MAX_CHARS } from '../constants';
import { ensureQueryLimit } from '../utils';
import { summarizeRows, MAIN_AGENT_COMPLETE_ROWS } from './data-summary';

// ============================================
// Source Agent
// ============================================

/**
 * Options to tune SourceAgent behavior when the MainAgent has already
 * resolved relevant tables via embedding search.
 *
 * - preResolvedSchema: replaces `tool.fullSchema` in the prompt — a compact
 *   block of just the tables relevant to this question.
 * - skipSchemaSearch: drops the search_schema tool (and reduces the
 *   iteration budget) because the LLM already has what it needs.
 */
export interface SourceAgentOptions {
	preResolvedSchema?: string;
	skipSchemaSearch?: boolean;
	/** Per-turn cancellation signal, threaded into this agent's own LLM call. */
	signal?: AbortSignal;
}

export class SourceAgent {
	private tool: ExternalTool;
	private config: AgentConfig;
	private streamBuffer: StreamBuffer;
	private attempts: number = 0;
	private options: SourceAgentOptions;

	constructor(
		tool: ExternalTool,
		config: AgentConfig,
		streamBuffer: StreamBuffer,
		options?: SourceAgentOptions,
	) {
		this.tool = tool;
		this.config = config;
		this.streamBuffer = streamBuffer;
		this.options = options || {};
	}

	/**
	 * Execute a query against this source based on the intent from the main agent.
	 *
	 * Flow:
	 * 1. Build prompt with full schema (from tool.description) + intent
	 * 2. Source agent's OWN LLM generates query via tool calling
	 * 3. Execute with retry — all handled internally
	 * 4. Return SourceAgentResult with data + isLimited metadata
	 *
	 * For very_large tier: LLM first calls search_schema to discover tables,
	 * then writes SQL with correct table/column names.
	 */
	async execute(input: SourceToolInput): Promise<SourceAgentResult> {
		const startTime = Date.now();
		const { intent, aggregation = 'raw' } = input;

		logger.info(`[SourceAgent:${this.tool.name}] Starting | intent: "${intent}" | aggregation: ${aggregation}`);

		try {
			// Build prompt using template from backend/.prompts/agent-source-query/
			const prompts = await this.buildPrompt(intent, aggregation);

			logger.logLLMPrompt(`sourceAgent:${this.tool.name}`, 'system', extractPromptText(prompts.system));
			logger.logLLMPrompt(`sourceAgent:${this.tool.name}`, 'user', prompts.user);

			// Build tool definitions — query tool + optional search_schema tool
			const tools = this.buildToolDefinitions();

			// Track executed tool info — collect ALL successful queries
			let executedTool: ExecutedToolInfo | null = null;
			let allExecutedTools: ExecutedToolInfo[] = [];
			let resultData: any[] = [];
			let queryExecuted: string | undefined;
			let totalRowsMatched = 0;
			let querySucceeded = false; // Tracks whether a query has succeeded (for streaming label)
			let successfulQueries = 0;  // Count of successful queries (max 2: primary + follow-up)

			// Schema search function (available for large + very_large tiers).
			// Kept available even when the MainAgent pre-resolved the schema, so the
			// agent can recover when the pre-resolved tables miss what's needed.
			// Only an explicit skipSchemaSearch (rare) suppresses it.
			const schemaSearchFn = this.options.skipSchemaSearch
				? undefined
				: ((this.tool as any).schemaSearchFn as
					| ((keywords: string[]) => string)
					| undefined);

			// Create tool handler — routes to query execution or schema search
			const toolHandler = async (toolName: string, toolInput: any): Promise<string> => {
				// Handle schema search tool (does NOT count as a retry attempt)
				if (toolName.endsWith('_search_schema') && schemaSearchFn) {
					const keywords = toolInput.keywords || [];
					logger.info(`[SourceAgent:${this.tool.name}] Schema search: ${keywords.join(', ')}`);

					if (this.streamBuffer.hasCallback()) {
						this.streamBuffer.write(`🔍 **Looking up tables:** ${keywords.join(', ')}\n\n`);
						await streamDelay();
					}

					const result = schemaSearchFn(keywords);

					return `Schema search results:\n\n${result}`;
				}

				// User aborted — don't START a new data-source query. (A query already
				// in flight can't be cancelled by the signal — that needs driver-level
				// cancellation — but this stops us from firing another one. The main
				// agent's between-iteration checkpoint then unwinds the turn.)
				if (this.options.signal?.aborted) {
					return `⛔ Request aborted by the user. Do not call any more tools.`;
				}

				// Handle query execution tool
				// Block if already got 2 successful queries (1 primary + 1 follow-up)
				if (successfulQueries >= 2) {
					return `⛔ Maximum successful queries reached (2). Use the data already retrieved.`;
				}

				this.attempts++;

				if (this.attempts > this.config.maxRetries) {
					throw new Error(`Max retry attempts (${this.config.maxRetries}) reached for ${this.tool.name}`);
				}

				if (this.attempts > 1 && this.streamBuffer.hasCallback()) {
					if (querySucceeded) {
						// Previous query succeeded — this is a follow-up for more data
						const reason = toolInput.reason || '';
						this.streamBuffer.write(`\n\n📝 **Follow-up query${reason ? `:** ${reason}` : '**'}\n\n`);
					} else {
						// Previous query failed — this is an error retry
						this.streamBuffer.write(`\n\n🔄 **Retrying query** (attempt ${this.attempts}/${this.config.maxRetries})\n\n`);
					}
					await streamDelay();
				}

				// Enforce the SERVER-SIDE fetch cap (not the small LLM-show cap).
				// We fetch the full result so it can be summarized completely; only
				// a small/complete slice is later inlined into the main agent's
				// context (see formatResultForMainAgent). Capping the SQL at the
				// tiny show-limit is what truncated lookups and caused the main
				// agent to generalize from a biased head-slice.
				const cappedInput = { ...toolInput };
				if (cappedInput.limit === undefined || cappedInput.limit > this.config.maxRowsFetched) {
					cappedInput.limit = this.config.maxRowsFetched;
				}
				// Enforce the cap IN the SQL text too. The agent often writes a big
				// LIMIT (e.g. 500) chasing "all rows"; capping only the `limit` param
				// left the executed/displayed SQL saying LIMIT 500 and reported a
				// misleading "N of 500 total". ensureQueryLimit rewrites an over-cap
				// LIMIT down to the fetch cap, keeps a smaller one, and adds one if
				// absent — so the SQL we run, show, and count are all consistent.
				const _st = this.extractSourceType();
				if (typeof cappedInput.sql === 'string') {
					cappedInput.sql = ensureQueryLimit(cappedInput.sql, this.config.maxRowsFetched, this.config.maxRowsFetched, _st as any);
				}
				if (typeof cappedInput.query === 'string') {
					cappedInput.query = ensureQueryLimit(cappedInput.query, this.config.maxRowsFetched, this.config.maxRowsFetched, _st as any);
				}

				// Track the query for metadata
				queryExecuted = cappedInput.sql || cappedInput.query || JSON.stringify(cappedInput);

				// Stream the generated query
				if (this.streamBuffer.hasCallback() && queryExecuted) {
					const queryDisplay = cappedInput.sql || cappedInput.query;
					if (queryDisplay) {
						this.streamBuffer.write(`📝 **Generated SQL query:**\n\`\`\`sql\n${queryDisplay}\n\`\`\`\n\n`);
					}
					await streamDelay();
				}

				let queryStartTime = Date.now();
				try {
					// Send timer markers — frontend renders "⏳ Executing query" label with a live timer pill
					if (this.streamBuffer.hasCallback()) {
						this.streamBuffer.write(`__QUERY_TIMER_START_Executing query__`);
					}
					queryStartTime = Date.now();
					const result = await this.tool.fn(cappedInput);
					if (this.streamBuffer.hasCallback()) {
						const querySeconds = ((Date.now() - queryStartTime) / 1000).toFixed(1);
						this.streamBuffer.write(`__QUERY_TIMER_DONE_${querySeconds}__\n\n`);
					}

					// Check for error in result object (some tools return { error: ... } instead of throwing)
					if (result && result.error) {
						const errorMsg = typeof result.error === 'string' ? result.error : JSON.stringify(result.error);
						logger.warn(`[SourceAgent:${this.tool.name}] Tool returned error (attempt ${this.attempts}/${this.config.maxRetries}): ${errorMsg}`);
						if (this.streamBuffer.hasCallback()) {
							this.streamBuffer.write(`❌ **Query failed:** ${errorMsg}\n\n`);
						}
						return `❌ ERROR: ${errorMsg}\n\nAnalyze the error and try again with a corrected query.`;
					}

					// Extract data
					resultData = result.data || [];
					totalRowsMatched = result.metadata?.totalCount || result.count || resultData.length;
					querySucceeded = true;
					successfulQueries++;

					// Stream success with data preview
					if (this.streamBuffer.hasCallback()) {
						const totalInfo = totalRowsMatched > resultData.length ? ` of ${totalRowsMatched} total` : '';
						this.streamBuffer.write(`✅ **${resultData.length} rows${totalInfo} from ${this.tool.name}**\n\n`);

						// Stream data preview as DataTable (frontend renders with 2-row preview + "Show All" button).
						// Bounded to the canonical stream-preview limits (rows AND chars/field) so a
						// wide/large result can't bloat the stream — same shape as every other preview.
						if (resultData.length > 0) {
							try {
								const preview = formatQueryResultForLLM(resultData, {
									maxRows: STREAM_PREVIEW_MAX_ROWS,
									maxCharsPerField: STREAM_PREVIEW_MAX_CHARS,
								});
								this.streamBuffer.write(`<DataTable>${JSON.stringify(preview.data)}</DataTable>\n\n`);
							} catch {
								// BigInt, circular refs, or other serialization issues — skip DataTable
								this.streamBuffer.write(`_Data preview not available_\n\n`);
							}
						}
					}

					// Build executed tool info (for component generation compatibility)
					const formattedResult = formatToolResultForLLM(result, {
						toolName: this.tool.name,
						maxRows: 5,
						maxCharsPerField: 200,
					});

					executedTool = {
						id: this.tool.id,
						name: this.tool.name,
						params: cappedInput,
						result: {
							_totalRecords: totalRowsMatched,
							_recordsShown: resultData.length,
							_metadata: result.metadata,
							_sampleData: resultData.slice(0, TOOL_TRACKING_SAMPLE_ROWS),
							// For the main agent: a bounded summary over the FULL fetched
							// result (complete structure regardless of size) + a complete
							// slice for small results (so lookups arrive whole, not biased).
							_summary: summarizeRows(resultData),
							_mainAgentRows: resultData.slice(0, MAIN_AGENT_COMPLETE_ROWS),
						},
						outputSchema: this.tool.outputSchema,
						sourceSchema: this.tool.description,
						sourceType: this.extractSourceType(),
					};
					allExecutedTools.push(executedTool);

					// Return success signal with context on whether follow-up is allowed
					const formatted = typeof formattedResult === 'string' ? formattedResult : JSON.stringify(formattedResult);
					const followUpNote = successfulQueries < 2
						? 'STOP now and return this data — it satisfies the intent in the common case. Make a second query ONLY if a column or row the intent explicitly requires is verifiably MISSING from these results (not merely to widen, re-sort, or double-check). A redundant follow-up wastes a full round-trip.'
						: 'Maximum queries reached. Use the data you have.';
					return `✅ Query executed successfully. ${resultData.length} rows returned (${totalRowsMatched} total matched). ${followUpNote}\n\n${formatted}`;

				} catch (execError) {
					const errorMsg = execError instanceof Error
						? execError.message
						: (typeof execError === 'object' && execError !== null)
							? (execError as any).message || (execError as any).error || JSON.stringify(execError)
							: String(execError);
					logger.warn(`[SourceAgent:${this.tool.name}] Tool execution failed (attempt ${this.attempts}/${this.config.maxRetries}): ${errorMsg}`);
					if (this.streamBuffer.hasCallback()) {
						// Close the timer before showing the error — otherwise the frontend spinner runs forever
						const querySeconds = ((Date.now() - queryStartTime) / 1000).toFixed(1);
						this.streamBuffer.write(`__QUERY_TIMER_DONE_${querySeconds}__\n\n`);
						this.streamBuffer.write(`❌ **Query failed:** ${errorMsg}\n\n`);
					}
					// Return error to LLM so it can analyze and retry with a corrected query
					return `❌ ERROR: ${errorMsg}\n\nAnalyze the error and try again with a corrected query.`;
				}
			};

			// maxIterations = total LLM round-trips allowed.
			// Each query attempt may need a preceding schema search, so they're paired.
			// With schema search: LLM often does search→query→search→query→response
			// Budget must cover: 2 successful queries (cap) + retries + schema searches + final response
			// With maxRetries=2, hasSchemaSearch: (2+2 queries × 2 cost) + 2 extra searches + 1 = 11
			// Without schema search: (2+2 queries × 1 cost) + 1 = 5
			const hasSchemaSearch = !!schemaSearchFn;
			const totalQueryAttempts = this.config.maxRetries + 2; // initial + retries + 1 follow-up
			const perQueryCost = hasSchemaSearch ? 2 : 1;          // schema search + query per attempt
			const extraSearches = hasSchemaSearch ? 2 : 0;         // LLM may do extra searches for broader intents
			const responseIterations = 1;                          // final response
			const maxIterations = (totalQueryAttempts * perQueryCost) + extraSearches + responseIterations;

			await LLM.streamWithTools(
				{ sys: prompts.system, user: prompts.user },
				tools,
				toolHandler,
				{
					model: this.config.sourceAgentModel || undefined,
					maxTokens: 2048,
					temperature: 0,
					apiKey: this.config.apiKey,
					signal: this.options.signal,
				},
				maxIterations
			);

			const executionTimeMs = Date.now() - startTime;

			// If tool was never called, return empty result
			if (!executedTool) {
				logger.warn(`[SourceAgent:${this.tool.name}] LLM did not call the tool`);
				return {
					sourceId: this.tool.id,
					sourceName: this.tool.name,
					success: false,
					data: [],
					metadata: {
						totalRowsMatched: 0,
						rowsReturned: 0,
						isLimited: false,
						executionTimeMs,
					},
					executedTool: this.buildEmptyExecutedTool(),
					error: 'Source agent did not execute any query',
				};
			}

			logger.info(`[SourceAgent:${this.tool.name}] Success | ${resultData.length} rows in ${executionTimeMs}ms`);

			return {
				sourceId: this.tool.id,
				sourceName: this.tool.name,
				success: true,
				// Don't retain the full fetched result — only the bounded slice the
				// main agent can actually use. The complete-structure summary and the
				// complete-small rows already live on each executedTool (`_summary` /
				// `_mainAgentRows`); keeping the full resultData here would hold up to
				// MAX_ROWS_FETCHED rows × every query in memory for the whole request
				// for no functional gain.
				data: resultData.slice(0, MAIN_AGENT_COMPLETE_ROWS),
				metadata: {
					totalRowsMatched,
					rowsReturned: resultData.length,
					isLimited: resultData.length < totalRowsMatched,
					queryExecuted,
					executionTimeMs,
				},
				executedTool,
				allExecutedTools,
			};

		} catch (error) {
			const executionTimeMs = Date.now() - startTime;
			const errorMsg = error instanceof Error ? error.message : String(error);

			logger.error(`[SourceAgent:${this.tool.name}] Failed: ${errorMsg}`);

			if (this.streamBuffer.hasCallback()) {
				this.streamBuffer.write(`\n\n❌ **Could not retrieve data from ${this.tool.name}:** ${errorMsg}\n\n`);
			}

			return {
				sourceId: this.tool.id,
				sourceName: this.tool.name,
				success: false,
				data: [],
				metadata: {
					totalRowsMatched: 0,
					rowsReturned: 0,
					isLimited: false,
					queryExecuted: undefined,
					executionTimeMs,
				},
				executedTool: this.buildEmptyExecutedTool(),
				error: errorMsg,
			};
		}
	}

	// ============================================
	// Private Helpers
	// ============================================

	/**
	 * Build prompt using the prompt loader (file system → hardcoded fallback in prompts.ts).
	 */
	private async buildPrompt(intent: string, aggregation: string): Promise<{ system: any; user: string }> {
		const sourceName = this.tool.name;
		const sourceType = this.extractSourceType();
		// When MainAgent pre-resolved tables via embedding search, prefer the compact
		// pre-resolved block. Otherwise use the full schema (all columns).
		const fullSchema = this.options.preResolvedSchema
			|| (this.tool as any).fullSchema
			|| this.tool.description
			|| 'No schema available';

		// Load database-specific SQL rules for this source type
		const databaseRules = await promptLoader.loadDatabaseRulesForType(sourceType);

		// Database-specific row limit syntax instruction
		const rowLimitSyntax: Record<string, string> = {
			'mssql': `Use SELECT TOP ${this.config.maxRowsFetched} in every SELECT statement`,
			'postgres': `Add LIMIT ${this.config.maxRowsFetched} at the end of every query`,
			'mysql': `Add LIMIT ${this.config.maxRowsFetched} at the end of every query`,
			'excel': `Add LIMIT ${this.config.maxRowsFetched} at the end of every query`,
			'csv': `Add LIMIT ${this.config.maxRowsFetched} at the end of every query`,
		};
		const rowLimitInstruction = rowLimitSyntax[sourceType] || `Limit results to ${this.config.maxRowsFetched} rows`;

		// Build schema search instructions based on tier.
		// If MainAgent pre-resolved the schema, we skip search_schema entirely.
		const hasSchemaSearch = !this.options.skipSchemaSearch && !!(this.tool as any).schemaSearchFn;
		const schemaTier = (this.tool as any).schemaTier || '';
		let schemaSearchInstructions = '';
		if (this.options.preResolvedSchema) {
			schemaSearchInstructions = `## Pre-Resolved Schema — a starting point, NOT guaranteed complete
The tables above were pre-selected by an embedding search on the intent. They are a HINT — the search can miss the right table (e.g. a join/bridge table or a differently-named master).
- If they contain the tables and columns you need, write your SQL directly using these EXACT column names — do NOT invent names.
${hasSchemaSearch
				? `- If they do NOT contain what you need — wrong tables, a missing join table, or a column you can't find — call **search_schema** with relevant keywords to locate the correct tables/columns BEFORE writing the query. Do NOT force-fit a query onto these tables by guessing column names; search instead.`
				: `- If a needed column isn't shown, do NOT guess it — work only with the columns listed.`}`;
		} else if (hasSchemaSearch && schemaTier === 'very_large') {
			schemaSearchInstructions = `## Schema Search — REQUIRED
This source has a very large schema. The schema above shows only table names — no column details.
**You MUST use search_schema BEFORE writing any query.** Do NOT guess or fabricate column names.
1. FIRST: Call search_schema with keywords related to the requested data
2. Review the returned column details, types, and sample values carefully
3. THEN write your SQL query using the EXACT column names from the search results

**CRITICAL: Column names in this database may NOT follow predictable patterns.**
- Do NOT assume column name prefixes based on table name abbreviations
- Do NOT invent column names by combining a table alias with a guessed suffix
- ONLY use column names that appeared in search_schema results — copy them exactly
- If you need columns you haven't searched for, call search_schema again before writing the query

You may search multiple times with different keywords if the first search doesn't find what you need.`;
		} else if (hasSchemaSearch) {
			schemaSearchInstructions = `## Schema Search — REQUIRED
This source has a large schema. The top tables are shown in detail above, but not all tables have full column details.
**You MUST use search_schema BEFORE writing any query** to verify the exact column names for the tables you plan to query.
1. FIRST: Call search_schema with the table names or keywords you intend to use
2. Review the returned column names, types, and sample values
3. THEN write your SQL query using the EXACT column names from the search results
Even if a table appears in the detailed schema above, search_schema returns sample values that help you write correct WHERE filters.
**Never skip the search step — it prevents failed queries and retries.**`;
		}

		// For Excel/CSV sources, clarify that the SQL engine is DuckDB
		const displaySourceType = (sourceType === 'excel' || sourceType === 'csv')
			? `${sourceType} — uses DuckDB SQL engine`
			: sourceType;

		const prompts = await promptLoader.loadPrompts('agent-source-query', {
			SOURCE_NAME: sourceName,
			SOURCE_TYPE: displaySourceType,
			FULL_SCHEMA: fullSchema,
			DATABASE_RULES: databaseRules,
			ROW_LIMIT_SYNTAX: rowLimitInstruction,
			// NOTE: MAX_ROWS here governs the SQL row cap (the {{MAX_ROWS}} in the
			// "limit results to N rows" line), so it MUST match the fetch cap that
			// ROW_LIMIT_SYNTAX states — not the smaller LLM-show cap — or the prompt
			// contradicts itself ("max 10 rows" + "LIMIT 50") and the LLM picks its
			// own number. See maxRowsFetched override below.
			SCHEMA_SEARCH_INSTRUCTIONS: schemaSearchInstructions,
			MAX_ROWS: String(this.config.maxRowsFetched),
			AGGREGATION_MODE: aggregation,
			GLOBAL_KNOWLEDGE_BASE: this.config.globalKnowledgeBase || 'No global knowledge base available.',
			CURRENT_DATETIME: getCurrentDateTimeForPrompt(),
			KNOWLEDGE_BASE_CONTEXT: this.config.knowledgeBaseContext || 'No additional knowledge base context available.',
			INTENT: intent,
		});
		return { system: prompts.system, user: prompts.user };
	}

	/**
	 * Build all LLM tool definitions for this source agent.
	 * Returns: [queryTool] for most tiers, [queryTool, searchSchemaTool] for very_large.
	 */
	private buildToolDefinitions(): any[] {
		const tools = [this.buildLLMToolDefinition()];

		// Add schema search tool for large + very_large tiers.
		// Suppress when MainAgent pre-resolved tables via embedding search.
		if ((this.tool as any).schemaSearchFn && !this.options.skipSchemaSearch) {
			tools.push(this.buildSchemaSearchToolDefinition());
		}

		return tools;
	}

	/**
	 * Build the query tool definition from the external tool.
	 * Parses param descriptions like "string - Sheet name" or "array (optional) - Columns"
	 * to extract the correct JSON schema type and required/optional status.
	 */
	private buildLLMToolDefinition(): any {
		const properties: Record<string, any> = {};
		const required: string[] = [];

		const toolParams: Record<string, any> = (this.tool as any).params || {};
		Object.entries(toolParams).forEach(([key, typeOrValue]) => {
			const valueStr = String(typeOrValue).toLowerCase();

			// Extract type from description like "string - Sheet name" or "array (optional) - Columns"
			let schemaType = 'string';
			const typeMatch = valueStr.match(/^(string|number|integer|boolean|array|object)\b/);
			if (typeMatch) {
				schemaType = typeMatch[1];
			} else if (typeof typeOrValue === 'number') {
				schemaType = Number.isInteger(typeOrValue) ? 'integer' : 'number';
			} else if (typeof typeOrValue === 'boolean') {
				schemaType = 'boolean';
			}

			// Check if optional
			const isOptional = valueStr.includes('(optional)') || valueStr.includes('optional');

			// Use the full param value as description
			const description = typeof typeOrValue === 'string' ? typeOrValue : `Parameter: ${key}`;

			// Build property schema based on type
			if (schemaType === 'array') {
				properties[key] = { type: 'array', items: { type: 'string' }, description };
			} else if (schemaType === 'object') {
				properties[key] = { type: 'object', description };
			} else {
				properties[key] = { type: schemaType, description };
			}

			if (!isOptional) {
				required.push(key);
			}
		});

		// Add optional 'reason' field — LLM provides this on follow-up queries to explain intent
		properties['reason'] = {
			type: 'string',
			description: 'string (optional) - Brief reason for this query. Required for follow-up queries after a successful first query. Explain what additional data you need and why.',
		};

		return {
			name: this.tool.id,
			description: this.tool.description || `Query ${this.tool.name}`,
			input_schema: {
				type: 'object',
				properties,
				required: required.length > 0 ? required : undefined,
			},
		};
	}

	/**
	 * Build the search_schema tool definition for very_large tier sources.
	 * Allows the LLM to discover table/column names before writing SQL.
	 */
	private buildSchemaSearchToolDefinition(): any {
		return {
			name: `${this.tool.id}_search_schema`,
			description: 'Search the database schema for tables and columns matching keywords. Returns full column details for matching tables. Use this BEFORE writing a query to find exact table and column names.',
			input_schema: {
				type: 'object',
				properties: {
					keywords: {
						type: 'array',
						items: { type: 'string' },
						description: 'Keywords to search for in table names and column names (e.g., ["quotation", "amount", "customer"]). Use multiple keywords for broader results.',
					},
				},
				required: ['keywords'],
			},
		};
	}

	/**
	 * Extract source type from tool ID.
	 */
	private extractSourceType(): string {
		const match = this.tool.id.match(/^(\w+)-[a-f0-9]+_/);
		if (!match) return 'unknown';

		const raw = match[1].toLowerCase();

		// Normalize variant source types (e.g., mssql_bridge → mssql)
		if (raw.startsWith('mssql')) return 'mssql';
		if (raw.startsWith('postgres')) return 'postgres';
		if (raw.startsWith('mysql')) return 'mysql';
		if (raw.startsWith('snowflake')) return 'snowflake';

		return raw;
	}

	/**
	 * Build an empty ExecutedToolInfo for error cases.
	 */
	private buildEmptyExecutedTool(): ExecutedToolInfo {
		return {
			id: this.tool.id,
			name: this.tool.name,
			params: {},
			result: {
				_totalRecords: 0,
				_recordsShown: 0,
				_sampleData: [],
			},
			outputSchema: this.tool.outputSchema,
		};
	}
}
